import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CreditCard, Smartphone, QrCode, Mail, BarChart3, Users, Image as ImageIcon, Video, Globe, ArrowRight } from 'lucide-react';
import MarketingNav from '@/components/MarketingNav';
import Footer from '@/components/Footer';

const Resources = () => {
  const navigate = useNavigate();

  const features = [
    { icon: CreditCard, title: 'NFC Smart Cards', description: 'Premium physical cards with tap-to-share technology. Works with all modern smartphones.', benefits: ['Instant profile sharing', 'No app required', 'Professional impression'] },
    { icon: Smartphone, title: 'Digital Profiles', description: 'Create beautiful, mobile-optimized digital business cards with rich media.', benefits: ['Photo galleries', 'Video embeds', 'Custom branding'] },
    { icon: QrCode, title: 'QR Code Generation', description: 'Auto-generated QR codes for easy sharing at events or on marketing materials.', benefits: ['Downloadable codes', 'Trackable scans', 'Print-ready'] },
    { icon: Mail, title: 'Lead Capture', description: 'Collect contact information from everyone who views your profile.', benefits: ['Custom forms', 'CSV export', 'CRM ready'] },
    { icon: BarChart3, title: 'Analytics Dashboard', description: 'Track views, engagement, and see who is interested in connecting with you.', benefits: ['Daily view tracking', 'Geographic data', 'Click analytics'] },
    { icon: Users, title: 'Social Links', description: 'Connect all your social media profiles in one place.', benefits: ['10+ platforms', 'Custom icons', 'Click tracking'] },
    { icon: ImageIcon, title: 'Photo Galleries', description: 'Showcase your work, products, or portfolio with up to 6 photos.', benefits: ['Auto-compressed', '1:1 aspect ratio', 'Fast loading'] },
    { icon: Video, title: 'Video Embeds', description: 'Add up to 5 YouTube videos to tell your story or showcase products.', benefits: ['YouTube integration', 'Auto-preview', 'Mobile optimized'] },
    { icon: Globe, title: 'Custom Domains', description: 'Use your own domain for a professional branded experience.', benefits: ['yourname.com/card', 'SSL included', 'Easy setup'] },
  ];

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#0A0A0A]" data-testid="resources-page">
      <MarketingNav />

      <section className="pt-32 lg:pt-40 pb-16 px-6" data-testid="resources-hero">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Resources</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold tracking-tight leading-[0.95] mb-6">
            Everything you need to<br />
            <span className="text-[#B8935A]">stand out & connect.</span>
          </h1>
          <p className="text-lg text-[#525252] max-w-2xl mb-8">
            Every feature that makes Sol Digital the most refined digital business card platform.
          </p>
          <Button onClick={() => navigate('/signup')} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-14 px-8 font-semibold" data-testid="resources-cta">
            Get started free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#EBE6DC]/40 border-y border-[#0A0A0A]/10" data-testid="features-grid">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 border border-[#0A0A0A]/10 hover:border-[#B8935A]/40 transition-colors" data-testid={`resource-feature-${index}`}>
              <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] flex items-center justify-center mb-6">
                <feature.icon className="w-5 h-5 text-[#B8935A]" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-[#525252] text-sm leading-relaxed mb-4">{feature.description}</p>
              <ul className="space-y-1.5">
                {feature.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                    <span className="w-1 h-1 rounded-full bg-[#B8935A]" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6" data-testid="use-cases">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Use cases</p>
            <h2 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight leading-tight">
              Built for every kind of professional.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: 'Entrepreneurs', body: 'Make every introduction count. Share your story, capture leads, and grow your network effortlessly.' },
              { title: 'Sales Teams', body: 'Track every interaction. Know who\'s interested. Follow up faster with built-in lead capture and analytics.' },
              { title: 'Creatives', body: 'Showcase your portfolio with galleries and videos. Stand out with a branded digital presence.' },
            ].map((u) => (
              <div key={u.title} className="bg-white rounded-2xl p-6 border border-[#0A0A0A]/10">
                <h3 className="font-heading font-bold text-lg mb-3">{u.title}</h3>
                <p className="text-[#525252] text-sm leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-6xl font-heading font-bold tracking-tight leading-[0.95] mb-6">
            Ready to <span className="text-[#B8935A]">elevate</span> your networking?
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/signup')} className="bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full h-14 px-8 font-semibold">
              Start free trial
            </Button>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10 rounded-full h-14 px-8 font-semibold">
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;
